import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Building2, UserCheck, ShieldCheck, FileWarning, ArrowRight, Loader2 } from 'lucide-react';
import api from '../utils/api';

/**
 * Iter-213 P0 — Approval Discoverability surface for admin dashboard.
 *
 * Single card that aggregates the four pending-approval queues into
 * one prominent operational widget on /admin/dashboard. Each row has
 * a count badge + deep-link CTA so admins can act immediately rather
 * than guess where to find the request.
 *
 *   • Client requests        → /admin/client-approvals?status=pending
 *   • AI creators            → /admin/ai-creators?status=pending
 *   • Talent (IDV)           → /admin/idv-review
 *   • Compliance issues      → /admin/clock-in-reviews
 *
 * Counts are best-effort — if any endpoint fails the row degrades to
 * "—" rather than blanking the whole card.
 */
const PendingApprovalsCard = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCounts(); }, []);

  const fetchCounts = async () => {
    setLoading(true);
    const out = {};
    // Run all four counts in parallel; tolerate failures silently.
    const probes = [
      ['client',     '/admin/client-approvals?status=pending',  (d) => d?.counts?.pending ?? (d?.clients || []).length],
      ['ai_creator', '/admin/ai-creators?status=pending',       (d) => (Array.isArray(d) ? d.length : (d?.length ?? 0))],
      // Iter-246b — IDV row removed per Mel; the admin home no longer
      // surfaces IDV counters. Reviewers navigate to /admin/idv-review.
      ['compliance', '/attendance/clock-in-reviews/count',      (d) => Object.values(d?.breakdown || {}).reduce((a,b)=>a+(b||0),0)],
    ];
    await Promise.all(probes.map(async ([key, path, pick]) => {
      try {
        const r = await api.get(path);
        out[key] = pick(r.data);
      } catch {
        out[key] = null;
      }
    }));
    setCounts(out);
    setLoading(false);
  };

  const rows = useMemo(() => ([
    {
      key: 'client',
      label: 'Client Requests',
      sub:   'New business signups awaiting approval',
      Icon:  Building2,
      tone:  'text-cyan-600',
      bg:    'bg-cyan-50 border-cyan-200',
      to:    '/admin/client-approvals?status=pending',
      testid: 'pending-approvals-client',
    },
    {
      key: 'ai_creator',
      label: 'AI Creators',
      sub:   'Designers / avatar creators awaiting onboarding review',
      Icon:  UserCheck,
      tone:  'text-violet-600',
      bg:    'bg-violet-50 border-violet-200',
      to:    '/admin/ai-creators?status=pending',
      testid: 'pending-approvals-ai-creators',
    },
    {
      key: 'compliance',
      label: 'Compliance Issues',
      sub:   'GPS-only / failed selfies / clock-in disputes',
      Icon:  FileWarning,
      tone:  'text-rose-600',
      bg:    'bg-rose-50 border-rose-200',
      to:    '/admin/clock-in-reviews',
      testid: 'pending-approvals-compliance',
    },
  ]), []);

  return (
    <Card className="bg-white border-gray-200 shadow-sm mb-6" data-testid="pending-approvals-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pending Approvals</h2>
            <p className="text-xs text-gray-500">Operational queues that need an admin decision</p>
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {rows.map(r => {
            const n = counts[r.key];
            const isBlocking = typeof n === 'number' && n > 0;
            return (
              <button
                key={r.key}
                onClick={() => navigate(r.to)}
                className={`relative text-left rounded-lg border ${r.bg} p-3 hover:shadow-sm transition-all`}
                data-testid={r.testid}
              >
                <div className="flex items-start justify-between mb-2">
                  <r.Icon className={`w-5 h-5 ${r.tone}`} />
                  <Badge
                    className={isBlocking
                      ? 'bg-rose-500 hover:bg-rose-500 text-white'
                      : 'bg-emerald-100 text-emerald-700'}
                    data-testid={`${r.testid}-count`}
                  >
                    {n === null ? '—' : (isBlocking ? n : 'all clear')}
                  </Badge>
                </div>
                <p className="font-semibold text-gray-900 text-sm">{r.label}</p>
                <p className="text-xs text-gray-600 mt-0.5 leading-tight">{r.sub}</p>
                <p className="text-xs font-medium text-gray-700 mt-2 inline-flex items-center gap-1">
                  Open queue <ArrowRight className="w-3 h-3" />
                </p>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCounts}
            data-testid="pending-approvals-refresh"
          >
            Refresh counts
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingApprovalsCard;
